export const SESSION_EXPIRED_EVENT = 'servernox:session-expired'
export const SESSION_NOTICE_KEY = 'servernox:session-notice'

export function jwtExpiryMillis(token: string | null): number | null {
  if (!token) return null
  try {
    const segment = token.split('.')[1] || ''
    const normalized = segment.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized + '='.repeat((4 - normalized.length % 4) % 4)
    const decode = atob(padded)
    const payload = JSON.parse(decode)
    const exp = Number(payload?.exp)
    return Number.isFinite(exp) && exp > 0 ? exp * 1000 : null
  } catch {
    return null
  }
}

export function isExpiredJwt(token: string | null, now = Date.now()): boolean {
  const expiresAt = jwtExpiryMillis(token)
  return expiresAt !== null && expiresAt <= now
}

export function isSessionFailure(status: number | undefined, detail: unknown, token: string | null): boolean {
  if (status === 401) return true
  if (status !== 403) return false
  if (isExpiredJwt(token)) return true

  const message = typeof detail === 'string' ? detail.toLocaleLowerCase('tr-TR') : ''
  return /could not validate credentials|not authenticated|geçersiz (oturum|kimlik|token)|oturum(un)? süresi|token (expired|invalid)/i.test(message)
}

/**
 * A late response must only expire the exact session that authorized its
 * request. Without this binding, an anonymous request started on the login
 * screen (or a request from a token that was just rotated) can return 401
 * after login and erase the new session from localStorage.
 */
export function shouldExpireSessionForRequest(
  status: number | undefined,
  detail: unknown,
  requestAuthorization: unknown,
  currentToken: string | null,
): boolean {
  if (!currentToken || typeof requestAuthorization !== 'string') return false
  const match = requestAuthorization.trim().match(/^Bearer\s+(.+)$/i)
  if (!match || match[1] !== currentToken) return false
  return isSessionFailure(status, detail, currentToken)
}

export function safeReturnPath(pathname: string, search = '', hash = ''): string {
  const value = `${pathname || '/'}${search}${hash}`
  if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/login')) return '/'
  return value
}

export function safeRequestedReturnPath(value: string | null): string {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/login')) return '/'
  return value
}

export function expiredLoginPath(pathname: string, search = '', hash = ''): string {
  const next = safeReturnPath(pathname, search, hash)
  return `/login?reason=expired&next=${encodeURIComponent(next)}`
}
